/**
 * Essai Premium en cours (bandeau du tableau de bord ; aucune étiquette d'accès pendant l'essai,
 * traité comme Premium) : affiché seulement quand l'état du compte est « essai » (LicenseService::etatAt),
 * que l'interrupteur de l'essai donne l'aperçu de 24 h ou l'essai de 14 jours. Les dates sont de
 * vrais instants (fin de l'essai) : heure du visiteur, comme le reste de l'espace connecté.
 */
export type LicenceEssai = {
  etat?: string;
  trialEndsAt?: string | null;
  trialStartedAt?: string | null;
};

export type EtatEssai = {
  fin: Date;
  /** Jour en cours (1 = jour de l'inscription), null si le début est inconnu. */
  jour: number | null;
  /** Durée totale en jours, jours bonus compris, null si le début est inconnu. */
  total: number | null;
  /** Essai de deux jours au plus (aperçu de 24 h) : l'heure de fin compte plus que le jour. */
  court: boolean;
};

const JOUR = 86_400_000;

export function etatEssai(licence: LicenceEssai | null | undefined, now: Date = new Date()): EtatEssai | null {
  if (!licence || licence.etat !== "essai" || !licence.trialEndsAt) return null;
  const fin = new Date(licence.trialEndsAt);
  if (Number.isNaN(fin.getTime()) || fin.getTime() <= now.getTime()) return null;
  const debut = licence.trialStartedAt ? new Date(licence.trialStartedAt) : null;
  if (!debut || Number.isNaN(debut.getTime()) || debut.getTime() > now.getTime()) {
    return { fin, jour: null, total: null, court: fin.getTime() - now.getTime() <= 2 * JOUR };
  }
  const total = Math.max(1, Math.round((fin.getTime() - debut.getTime()) / JOUR));
  const jour = Math.min(total, Math.floor((now.getTime() - debut.getTime()) / JOUR) + 1);

  return { fin, jour, total, court: total <= 2 };
}

const dateLongue = (d: Date): string => d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
const heure = (d: Date): string => `${d.getHours()} h ${String(d.getMinutes()).padStart(2, "0")}`;

/** Fin de l'essai en clair : « jusqu'au lundi 3 novembre (jour 4 sur 14) », ou avec l'heure pour l'aperçu. */
export function finEssai(e: EtatEssai): string {
  if (e.court) return `jusqu'au ${dateLongue(e.fin)} à ${heure(e.fin)}`;
  const compte = e.jour !== null && e.total !== null ? ` (jour ${e.jour} sur ${e.total})` : "";
  return `jusqu'au ${dateLongue(e.fin)}${compte}`;
}

/** Ligne du bandeau : jamais un second appel à payer, aucun compte à rebours. */
export function texteEssai(e: EtatEssai): string {
  return `Premium offert ${finEssai(e)}. Ensuite, votre compte reste gratuit, rien à résilier.`;
}
